import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useLocalData } from "../hooks/useLocalData";
import { onOrgIndividuals, onAgentIndividuals } from "../services/individualService";
import { ArrowLeft, Search, ChevronRight, Plus, X, UserPlus, Trash2, Mic, AlertTriangle } from "lucide-react";
import BottomNav from "../components/BottomNav";

export default function CaseloadPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { individuals: localIndividuals, deleteIndividual } = useLocalData();
  const [firestoreIndividuals, setFirestoreIndividuals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Pull individuals from Firestore — all for supervisors/admins, assigned-only for agents
  useEffect(() => {
    if (!user?.uid) return;
    const org = profile?.organization || localStorage.getItem("fieldnotes_user_org") || "default-org";
    const role = profile?.role || "agent";

    let unsub;
    if (role === "supervisor" || role === "admin") {
      // Supervisors and admins see all individuals in the org
      unsub = onOrgIndividuals(org, (inds) => {
        setFirestoreIndividuals(inds);
        setLoading(false);
      });
    } else {
      // Agents see only their assigned individuals
      unsub = onAgentIndividuals(user.uid, org, (inds) => {
        setFirestoreIndividuals(inds);
        setLoading(false);
      });
    }

    // Fallback timeout — if Firestore hasn't responded in 3 seconds, show local data
    const timeout = setTimeout(() => setLoading(false), 3000);

    return () => {
      if (unsub) unsub();
      clearTimeout(timeout);
    };
  }, [user, profile]);

  // Use Firestore individuals if available, fall back to localStorage
  const individuals = firestoreIndividuals !== null && firestoreIndividuals.length > 0
    ? firestoreIndividuals
    : localIndividuals;

  const filters = ["All", "Low Risk", "Moderate", "High Risk"];

  const filtered = individuals
    .filter((ind) => {
      if (filter === "Low Risk") return ind.riskLevel === "low";
      if (filter === "Moderate") return ind.riskLevel === "moderate";
      if (filter === "High Risk") return ind.riskLevel === "high";
      return true;
    })
    .filter((ind) =>
      searchQuery
        ? `${ind.firstName} ${ind.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
        : true
    )
    .sort((a, b) => `${a.lastName}`.localeCompare(`${b.lastName}`));

  const riskColor = (level) =>
    level === "high" ? "#fc8181" : level === "moderate" ? "#ecc94b" : "#68d391";

  const riskBg = (level) =>
    level === "high"
      ? "rgba(252,129,129,0.15)"
      : level === "moderate"
      ? "rgba(236,201,75,0.15)"
      : "rgba(72,187,120,0.15)";

  const handleDelete = () => {
    if (deleteConfirm) {
      deleteIndividual(deleteConfirm.id);
      setDeleteConfirm(null);
      setExpandedId(null);
    }
  };

  return (
    <div className="min-h-screen bg-surface-primary">
      <header
        className="sticky top-0 z-50 px-5 py-4 flex items-center justify-between"
        style={{
          background: "linear-gradient(135deg, #0f1729, #162033)",
          borderBottom: "1px solid rgba(99,179,237,0.1)",
        }}
      >
        <button className="flex items-center gap-3" onClick={() => navigate("/dashboard")}>
          <ArrowLeft size={20} className="text-gray-300" />
          <span className="text-lg font-bold text-gray-100">My Caseload</span>
        </button>
        <button
          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
          style={{ background: "linear-gradient(135deg, #3182ce, #2b6cb0)", color: "white" }}
          onClick={() => navigate("/onboarding")}
        >
          <Plus size={12} /> Add
        </button>
      </header>

      <div className="px-5 py-4 pb-28">
        {/* Search */}
        <div className="relative mb-3">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" />
          <input
            className="field-input pl-10"
            placeholder="Search individuals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className="px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors"
              style={{
                background: filter === f ? "rgba(49,130,206,0.2)" : "rgba(15,23,42,0.5)",
                color: filter === f ? "#63b3ed" : "#718096",
                border: `1px solid ${filter === f ? "rgba(49,130,206,0.3)" : "rgba(99,179,237,0.06)"}`,
              }}
            >
              {f}
            </button>
          ))}
          <span className="px-3.5 py-1.5 text-xs text-gray-600">
            {filtered.length} individual{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Individual Cards */}
        <div className="stagger-children">
          {filtered.map((ind) => (
            <div key={ind.id} className="glass-card mb-3 overflow-hidden">
              {/* Main card — tap to go to profile */}
              <div
                className="p-4"
                onClick={() => navigate(`/individual/${ind.id}`)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{
                          background: riskColor(ind.riskLevel),
                          boxShadow: `0 0 6px ${riskColor(ind.riskLevel)}80`,
                        }}
                      />
                      <span className="text-base font-bold text-gray-100">
                        {ind.firstName} {ind.lastName}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1.5">
                      DOB: {ind.dob || "—"} · {ind.program}
                    </div>
                    {ind.address && (
                      <div className="text-xs text-gray-400 mt-0.5">{ind.address}</div>
                    )}
                    <div className="flex gap-1.5 mt-2">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase"
                        style={{ background: riskBg(ind.riskLevel), color: riskColor(ind.riskLevel) }}
                      >
                        {ind.riskLevel} risk
                      </span>
                      {ind.medications && ind.medications.length > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/10 text-blue-400">
                          {ind.medications.length} med{ind.medications.length !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight
                    size={16}
                    className="text-gray-600 mt-1 transition-transform"
                    style={{ transform: expandedId === ind.id ? "rotate(90deg)" : "rotate(0deg)" }}
                  />
                </div>
              </div>

              {/* Expanded action area */}
              {expandedId === ind.id && (
                <div
                  className="px-4 pb-4 pt-0 animate-fade-in"
                  style={{ borderTop: "1px solid rgba(99,179,237,0.06)" }}
                >
                  {/* Details */}
                  {ind.medications && ind.medications.length > 0 && (
                    <div className="mb-2 mt-3">
                      <div className="text-[10px] text-gray-600 font-semibold uppercase mb-1">Medications</div>
                      <div className="text-xs text-gray-400">{ind.medications.join(", ")}</div>
                    </div>
                  )}
                  {ind.diagnoses && ind.diagnoses.length > 0 && (
                    <div className="mb-3">
                      <div className="text-[10px] text-gray-600 font-semibold uppercase mb-1">Diagnoses</div>
                      <div className="text-xs text-gray-400">{ind.diagnoses.join(", ")}</div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-2">
                    <button
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold text-white"
                      style={{ background: "linear-gradient(135deg, #3182ce, #2b6cb0)" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/session/new/" + ind.id, {
                          state: {
                            individualId: ind.id,
                            individualName: `${ind.firstName} ${ind.lastName}`,
                            dob: ind.dob,
                            locationType: "Out-of-Clinic",
                            location: ind.address,
                            serviceType: "ICM Weekly Visit",
                            knownMedications: ind.medications || [],
                            knownDiagnoses: ind.diagnoses || [],
                          },
                        });
                      }}
                    >
                      <Mic size={13} /> Start Session
                    </button>
                    <button
                      className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                      style={{
                        background: "rgba(252,129,129,0.06)",
                        border: "1px solid rgba(252,129,129,0.15)",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(ind);
                      }}
                    >
                      <Trash2 size={15} className="text-red-400" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center mt-12">
            <UserPlus size={32} className="text-gray-700 mx-auto mb-3" />
            <div className="text-sm text-gray-500 mb-3">
              {searchQuery ? "No individuals match your search." : "No individuals in your caseload yet."}
            </div>
            {!searchQuery && (
              <button
                className="px-4 py-2 rounded-xl text-sm font-semibold"
                style={{ background: "rgba(99,179,237,0.1)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)" }}
                onClick={() => navigate("/onboarding")}
              >
                Add Your First Individual
              </button>
            )}
          </div>
        )}
      </div>

      {/* ===== DELETE CONFIRM ===== */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-8">
          <div
            className="w-full max-w-sm rounded-2xl p-6"
            style={{
              background: "linear-gradient(135deg, #0f1729, #162033)",
              border: "1px solid rgba(252,129,129,0.2)",
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-yellow-400" />
              <h3 className="text-lg font-bold text-gray-100">Move to Trash?</h3>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-1">
              Move <span className="text-gray-200 font-semibold">{deleteConfirm.firstName} {deleteConfirm.lastName}</span> to the trash?
            </p>
            <p className="text-xs text-gray-600 mb-5">
              They will be kept in Trash for 30 days and can be restored anytime. Scheduled appointments will be removed. Submitted notes are not affected.
            </p>
            <div className="flex gap-3">
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold"
                style={{ background: "rgba(99,179,237,0.08)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)" }}
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white"
                style={{ background: "linear-gradient(135deg, #b7791f, #975a16)" }}
                onClick={handleDelete}
              >
                Move to Trash
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav active="caseload" />
    </div>
  );
}